import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { calendlyService } from '../../../services/calendlyService';

const SchedulingCalendar = ({ selectedType, onSelectSlot, selectedSlot }) => {
  const [currentWeek, setCurrentWeek] = useState(0);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calendlyAvailability, setCalendlyAvailability] = useState([]);
  const [useCalendly, setUseCalendly] = useState(false);

  // Fetch booked appointments from database
  useEffect(() => {
    const fetchBookedSlots = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          ?.from('marketing_support_bookings')
          ?.select('appointment_date, appointment_time')
          ?.order('appointment_date', { ascending: true });

        if (error) {
          console.error('Error fetching bookings:', error);
        } else {
          setBookedSlots(data || []);
        }

        // Check if Calendly is configured and fetch availability
        if (calendlyService?.isConfigured()) {
          setUseCalendly(true);
          await fetchCalendlyAvailability();
        }
      } catch (err) {
        console.error('Failed to fetch bookings:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBookedSlots();
  }, [currentWeek]);

  const fetchCalendlyAvailability = async () => {
    try {
      const weekDays = generateWeekDays(currentWeek);
      const startDate = weekDays?.[0]?.date;
      const endDate = weekDays?.[4]?.date;

      // Get event types first
      const eventTypes = await calendlyService?.getEventTypes();
      if (eventTypes?.length > 0) {
        // Use first active event type for availability
        const availability = await calendlyService?.getAvailability(
          eventTypes?.[0]?.id,
          `${startDate}T00:00:00Z`,
          `${endDate}T23:59:59Z`
        );
        setCalendlyAvailability(availability);
      }
    } catch (error) {
      console.error('Error fetching Calendly availability:', error);
      setUseCalendly(false);
    }
  };

  // Get current date/time in PST
  const getCurrentPSTDate = () => {
    const now = new Date();
    // Convert to PST (UTC-8 or UTC-7 during DST)
    const pstDate = new Date(now?.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
    return pstDate;
  };

  const generateTimeSlots = () => {
    const slots = [];
    const startHour = 9;
    const endHour = 17;
    const interval = 30;

    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += interval) {
        const time = `${hour?.toString()?.padStart(2, '0')}:${minute?.toString()?.padStart(2, '0')}`;
        const displayTime = new Date(`2026-01-20T${time}`)?.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        slots?.push({ time, displayTime });
      }
    }
    return slots;
  };

  const generateWeekDays = (weekOffset) => {
    const days = [];
    const currentPST = getCurrentPSTDate();
    const startDate = new Date(currentPST);
    startDate?.setDate(currentPST?.getDate() + weekOffset * 7);

    for (let i = 0; i < 5; i++) {
      const date = new Date(startDate);
      date?.setDate(startDate?.getDate() + i);
      days?.push({
        date: date?.toISOString()?.split('T')?.[0],
        dayName: date?.toLocaleDateString('en-US', { weekday: 'short' }),
        dayNumber: date?.getDate(),
        month: date?.toLocaleDateString('en-US', { month: 'short' }),
        fullDate: date
      });
    }
    return days;
  };

  const timeSlots = generateTimeSlots();
  const weekDays = generateWeekDays(currentWeek);

  // Check if a slot is available (not booked and not in the past)
  const isSlotAvailable = (date, time, fullDate) => {
    // Check if slot is already booked
    const isBooked = bookedSlots?.some(
      (booking) => booking?.appointment_date === date && booking?.appointment_time === time
    );

    if (isBooked) {
      return false;
    }

    // If Calendly is configured, check against Calendly availability
    if (useCalendly && calendlyAvailability?.length > 0) {
      const [hours, minutes] = time?.split(':')?.map(Number) || [0, 0];
      const slotDateTime = new Date(fullDate);
      slotDateTime?.setHours(hours, minutes, 0, 0);

      // Check if this time slot exists in Calendly availability
      const isCalendlyAvailable = calendlyAvailability?.some(slot => {
        const calendlyStart = new Date(slot?.startTime);
        const calendlyEnd = new Date(slot?.endTime);
        return slotDateTime >= calendlyStart && slotDateTime < calendlyEnd;
      });

      if (!isCalendlyAvailable) {
        return false;
      }
    }

    // Check if slot is in the past
    const currentPST = getCurrentPSTDate();
    const [hours, minutes] = time?.split(':')?.map(Number);
    const slotDateTime = new Date(fullDate);
    slotDateTime?.setHours(hours, minutes, 0, 0);

    // Slot must be at least 1 hour in the future
    const oneHourFromNow = new Date(currentPST?.getTime() + 60 * 60 * 1000);
    
    return slotDateTime > oneHourFromNow;
  };

  const handleSlotClick = (date, time, displayTime, dayName, dayNumber, month, fullDate) => {
    if (isSlotAvailable(date, time, fullDate)) {
      onSelectSlot({
        date,
        time,
        displayTime,
        dayName,
        dayNumber,
        month,
        fullDate: fullDate?.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      });
    }
  };

  const handlePreviousWeek = () => {
    if (currentWeek > 0) {
      setCurrentWeek(currentWeek - 1);
    }
  };

  const handleNextWeek = () => {
    setCurrentWeek(currentWeek + 1);
  };

  if (!selectedType) {
    return (
      <div className="bg-muted/50 rounded-lg p-8 md:p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--corvid-background-color, rgba(var(--bg, var(--color_17, 37, 99, 235)), var(--alpha-bg, 0.1)))' }}>
          <Icon name="Calendar" size={32} color="black" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">Select Support Type First</h3>
        <p className="text-sm text-muted-foreground">
          Please choose a consultation type above to view available appointment slots
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-muted/50 rounded-lg p-8 md:p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center animate-pulse" style={{ backgroundColor: 'var(--corvid-background-color, rgba(var(--bg, var(--color_17, 37, 99, 235)), var(--alpha-bg, 0.1)))' }}>
          <Icon name="Calendar" size={32} color="black" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">Loading Available Times...</h3>
        <p className="text-sm text-muted-foreground">
          {useCalendly ? 'Syncing with Calendly availability' : 'Checking calendar availability'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {useCalendly && (
        <div className="bg-success/10 border border-success/20 rounded-lg p-3 flex items-center gap-2">
          <Icon name="CheckCircle" size={16} className="text-success" />
          <p className="text-sm text-success font-medium">Synced with Calendly availability</p>
        </div>
      )}
      <div className="flex items-center justify-between gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={handlePreviousWeek}
          disabled={currentWeek === 0}
          iconName="ChevronLeft"
          iconPosition="left"
        >
          Previous
        </Button>

        <div className="text-center">
          <h3 className="text-lg font-semibold text-foreground">
            {weekDays?.[0]?.month} {weekDays?.[0]?.dayNumber} - {weekDays?.[4]?.month} {weekDays?.[4]?.dayNumber}, {weekDays?.[0]?.fullDate?.getFullYear()}
          </h3>
          <p className="text-sm text-muted-foreground font-caption">Select an available time slot</p>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={handleNextWeek}
          iconName="ChevronRight"
          iconPosition="right"
        >
          Next
        </Button>
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[600px]">
          <div className="grid grid-cols-6 gap-2 mb-4">
            <div className="text-xs font-medium text-muted-foreground font-caption">Time (PST)</div>
            {weekDays?.map((day) => (
              <div key={day?.date} className="text-center">
                <div className="text-sm font-semibold text-foreground">{day?.dayName}</div>
                <div className="text-xs text-muted-foreground font-caption">
                  {day?.month} {day?.dayNumber}
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
            {timeSlots?.map((slot) => (
              <div key={slot?.time} className="grid grid-cols-6 gap-2">
                <div className="flex items-center text-xs text-muted-foreground font-caption">
                  {slot?.displayTime}
                </div>
                {weekDays?.map((day) => {
                  const available = isSlotAvailable(day?.date, slot?.displayTime, day?.fullDate);
                  const isSelected =
                    selectedSlot?.date === day?.date && selectedSlot?.time === slot?.displayTime;

                  return (
                    <button
                      key={`${day?.date}-${slot?.time}`}
                      onClick={() =>
                        handleSlotClick(
                          day?.date,
                          slot?.displayTime,
                          slot?.displayTime,
                          day?.dayName,
                          day?.dayNumber,
                          day?.month,
                          day?.fullDate
                        )
                      }
                      disabled={!available}
                      className={`h-10 rounded-lg text-xs font-medium transition-all duration-250 ${
                        isSelected
                          ? 'bg-primary text-primary-foreground shadow-md'
                          : available
                          ? 'bg-card border border-border hover:border-primary hover:shadow-sm text-foreground'
                          : 'bg-muted/30 text-muted-foreground cursor-not-allowed'
                      }`}
                    >
                      {available ? (isSelected ? 'Selected' : 'Available') : '—'}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
      {selectedSlot && (
        <div className="border rounded-lg p-4" style={{ backgroundColor: 'var(--corvid-background-color, rgba(var(--bg, var(--color_17, 37, 99, 235)), var(--alpha-bg, 0.05)))', borderColor: 'var(--corvid-background-color, rgba(var(--bg, var(--color_17, 37, 99, 235)), var(--alpha-bg, 0.2)))' }}>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--corvid-background-color, rgba(var(--bg, var(--color_17, 37, 99, 235)), var(--alpha-bg, 0.1)))' }}>
              <Icon name="CheckCircle" size={20} color="black" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-1">Selected Appointment</h4>
              <p className="text-sm text-muted-foreground">
                {selectedSlot?.fullDate} at {selectedSlot?.displayTime} PST
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="bg-muted/50 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={18} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="mb-2">
              <strong className="text-foreground">Appointment Details:</strong>
            </p>
            <ul className="space-y-1 list-disc list-inside">
              <li>All times shown in Pacific Standard Time (PST)</li>
              <li>Only available time slots are shown (booked times are hidden)</li>
              <li>You'll receive a confirmation email with meeting details</li>
              <li>A calendar invite will be sent to your email</li>
              <li>Please join 5 minutes early to test your connection</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchedulingCalendar;